import { useEffect, useState } from 'react';
import type { Incarico } from '../types';
import { SettingsService } from '../services/apiService';
import { turnoPausaStato } from './turnoStato';

// Un incarico è concluso per il singolo collaboratore quando c'è un report per
// ogni giornata pianificata (dataInizio → dataFine), compresi i turni a cavallo
// della mezzanotte. Se dataFine è null si considera fino a oggi.
// Non dipende dallo stato del mandato, che resta "In corso" finché tutti gli incarichi non sono espletati.
export const isIncaricoConcluso = (inc: Incarico | any): boolean => {
    if (!inc) return false;
    // Gli stati finali hanno già il loro badge.
    if (['COMPLETATO', 'REVOCATO', 'RIFIUTATO'].includes(inc.stato)) return false;
    if (!inc.dataInizio) return false;

    const start = new Date(inc.dataInizio);
    start.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDeclared = inc.dataFine ? new Date(inc.dataFine) : null;
    if (endDeclared) endDeclared.setHours(0, 0, 0, 0);

    // Tutto il range pianificato; oggi solo se manca dataFine.
    const end = endDeclared || today;
    if (end < start) return false; // incarico futuro, ancora nulla da concludere

    // Date "yyyy-mm-dd" già coperte da una relazione del collaboratore.
    const reportedDays = new Set<string>(
        (inc.relazioni || [])
            .map((r: any) => typeof r?.dataRelazione === 'string' ? r.dataRelazione.slice(0, 10) : null)
            .filter(Boolean)
    );

    const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // Incarico continuativo: c'è un solo report, datato al giorno di inizio,
    // e basta quello.
    if (inc.continuativo) return reportedDays.has(dayKey(start));

    // Copertura giorno per giorno.
    const cursor = new Date(start);
    while (cursor <= end) {
        const dayStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        if (!reportedDays.has(dayStr)) return false;
        cursor.setDate(cursor.getDate() + 1);
    }
    return true;
};

// Stato operativo come lo vede il collaboratore in app, da mostrare anche all'admin.
// Restituisce null per gli stati non operativi: il chiamante usa il proprio badge.
// 'pronto' = in corso ma senza sessione aperta; 'pausa' = avviato e fermato nello
// stesso giorno, ancora riprendibile.
export type StatoOperativoKind = 'espletato' | 'report' | 'servizio' | 'pronto' | 'pausa';
export interface StatoOperativo { label: string; kind: StatoOperativoKind; }

const localTodayKey = (): string => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export const getStatoOperativoIncarico = (inc: any): StatoOperativo | null => {
    if (!inc) return null;
    const st = String(inc.stato || '').toUpperCase();
    if (['REVOCATO', 'RIFIUTATO', 'ASSEGNATO', 'ACCETTATO', 'COMPLETATO'].includes(st)) return null;
    const today = localTodayKey();
    const relazioni = inc.relazioni || [];
    const hasReportForToday = relazioni.some((r: any) => typeof r?.dataRelazione === 'string' && r.dataRelazione.slice(0, 10) === today);
    if (hasReportForToday || isIncaricoConcluso(inc)) return { label: 'Servizio Espletato', kind: 'espletato' };
    const sessioni = inc.sessioni || [];
    // "In Servizio" solo con una sessione aperta (start senza stop o activeSessionId).
    const hasOpen = sessioni.some((s: any) => s.startTime && !s.endTime) || !!inc.activeSessionId;
    const endStr = inc.dataFine ? String(inc.dataFine).split('T')[0] : '';
    const reportDaInviare = st === 'IN_CORSO' && !!endStr && endStr < today && !hasOpen
        && sessioni.some((s: any) => !!s.endTime) && relazioni.length === 0;
    if (reportDaInviare) return { label: 'Report', kind: 'report' };
    // Giornata passata e mai avviato → "Non avviato".
    const neverStarted = sessioni.length === 0;
    if (st === 'IN_CORSO' && !!endStr && endStr < today && neverStarted) return { label: 'Non avviato', kind: 'pronto' };
    if (st === 'IN_CORSO') {
        return hasOpen
            ? { label: 'In Servizio', kind: 'servizio' }
            : { label: 'Pronto', kind: 'pronto' };
    }
    return null;
};

// Stato operativo di un turno come lo vede il collaboratore.
// null per gli stati non operativi: il chiamante usa il proprio badge.
export const getStatoOperativoTurno = (t: any): StatoOperativo | null => {
    if (!t) return null;
    if (t.dataRevoca || t.statoAccettazione === 'RIFIUTATO') return null;
    const started = !!t.effettivoInizio;
    const ended = !!t.effettivoFine;
    // Report inviato → turno espletato (come isConcluso in TurnoCard).
    if (started && ended && t.reportTrasmesso === true) return { label: 'Servizio Espletato', kind: 'espletato' };
    // Start senza Stop → in corso.
    if (started && !ended) return { label: 'In Servizio', kind: 'servizio' };
    // Start+Stop senza report: nello stesso giorno è "In Pausa" (riprendibile),
    // nei giorni successivi "Report".
    if (started && ended) {
        return turnoPausaStato(t) === 'PAUSA'
            ? { label: 'In Pausa', kind: 'pausa' }
            : { label: 'Report', kind: 'report' };
    }
    return null;
};

export interface StatoBadge { label: string; cls: string; inactive: boolean; }

// Stato operativo (Servizio Espletato, Report, In Servizio...) con gli stessi colori
// della pagina del mandato e del servizio.
const badgeOperativo = (op: StatoOperativo): StatoBadge => ({
    label: op.label,
    cls: op.kind === 'espletato' ? 'bg-emerald-600 text-white border-emerald-700'
        : op.kind === 'report' ? 'bg-orange-100 text-orange-700 border-orange-200'
        : op.kind === 'pausa' ? 'bg-amber-100 text-amber-700 border-amber-200'
        : op.kind === 'pronto' ? 'bg-slate-100 text-slate-600 border-slate-200'
        : 'bg-emerald-100 text-emerald-700 border-emerald-200',
    inactive: false,
});

// Badge di un incarico investigativo (calendario, agenda collaboratore): prima lo stato
// operativo, come nella pagina del mandato (lo stato salvato resta IN_CORSO anche dopo
// il report), poi l'etichetta leggibile dello stato salvato; null se non rilevante.
// inactive = stato terminale (revocato/rifiutato), da attenuare.
export const getIncaricoStatoBadge = (inc: any): StatoBadge | null => {
    // Annullato insieme al mandato: prevale anche su un report già inviato.
    if (inc?.stato === 'ANNULLATO') return { label: 'Annullato', cls: 'bg-slate-100 text-slate-500 border-slate-200', inactive: true };
    const op = getStatoOperativoIncarico(inc);
    if (op) return badgeOperativo(op);
    const s = inc?.stato;
    if (s === 'REVOCATO') return { label: 'Revocato', cls: 'bg-rose-100 text-rose-700 border-rose-200', inactive: true };
    if (s === 'RIFIUTATO') return { label: 'Rifiutato', cls: 'bg-rose-50 text-rose-600 border-rose-200', inactive: true };
    if (s === 'ACCETTATO') return { label: 'Accettato', cls: 'bg-teal-100 text-teal-700 border-teal-200', inactive: false };
    if (s === 'ASSEGNATO') return { label: 'Da accettare', cls: 'bg-amber-100 text-amber-800 border-amber-200', inactive: false };
    if (s === 'IN_CORSO') return { label: 'In corso', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', inactive: false };
    if (s === 'COMPLETATO') return { label: 'Completato', cls: 'bg-emerald-600 text-white border-emerald-700', inactive: false };
    return null;
};

// Badge di un turno: dopo l'avvio lo stato operativo come nella pagina del servizio,
// prima revocato > rifiutato > accettato > assegnato, come in TurnoCard.
export const getTurnoStatoBadge = (t: any): StatoBadge | null => {
    const op = getStatoOperativoTurno(t);
    if (op) return badgeOperativo(op);
    if (t?.dataRevoca) return { label: 'Revocato', cls: 'bg-rose-100 text-rose-700 border-rose-200', inactive: true };
    const s = t?.statoAccettazione;
    if (s === 'RIFIUTATO') return { label: 'Rifiutato', cls: 'bg-rose-50 text-rose-600 border-rose-200', inactive: true };
    if (s === 'ACCETTATO') return { label: 'Accettato', cls: 'bg-teal-100 text-teal-700 border-teal-200', inactive: false };
    if (s === 'ASSEGNATO') return { label: 'Da accettare', cls: 'bg-amber-100 text-amber-800 border-amber-200', inactive: false };
    return null;
};

// Incarico mono-giorno: inizio e fine nello stesso giorno (o dataFine null).
// Serve a datare la relazione sul giorno dell'incarico anche se il collaboratore
// lavora il giorno prima.
export const isIncaricoSingleDay = (inc: Incarico | any): boolean => {
    if (!inc?.dataInizio) return false;
    const start = String(inc.dataInizio).split('T')[0];
    const end = inc.dataFine ? String(inc.dataFine).split('T')[0] : null;
    return end === null || start === end;
};

// Parte data (yyyy-mm-dd) di un timestamp ISO.
export const extractDateOnly = (iso?: string | null): string | null => {
    if (!iso) return null;
    return String(iso).split('T')[0] || null;
};

// Allerta "Orari difformi": scostamento tra orario pianificato e reale, solo segnalato.
// È la somma degli scarti su inizio (prima sessione) e fine (ultima sessione);
// con la sessione ancora aperta si confronta solo l'inizio.
// Restituisce null se il confronto non è possibile.
export const getOrariDifformiMinuti = (inc: Incarico | any): number | null => {
    if (!inc) return null;
    // Urgenti, autogenerati e incarichi con orari preset non hanno un orario
    // pianificato significativo: il confronto darebbe solo falsi allarmi.
    const tipoOrigine = (inc as any).tipoOrigine;
    if (tipoOrigine === 'URGENTE' || tipoOrigine === 'URGENTE_SENZA_MANDATO' || tipoOrigine === 'AUTOGENERATO') {
        return null;
    }
    if ((inc as any).orariPreset === true) {
        return null;
    }
    const sessioni = (inc.sessioni || []) as Array<{ startTime?: string; endTime?: string }>;
    if (sessioni.length === 0) return null;
    if (!inc.dataInizio) return null;

    const pianInizio = new Date(inc.dataInizio).getTime();
    const pianFine = inc.dataFine ? new Date(inc.dataFine).getTime() : null;

    // Prima sessione per startTime, ultima sessione per endTime
    const starts = sessioni
        .map(s => s.startTime ? new Date(s.startTime).getTime() : null)
        .filter((t): t is number => t !== null);
    if (starts.length === 0) return null;
    const realInizio = Math.min(...starts);

    let totaleMs = Math.abs(realInizio - pianInizio);

    if (pianFine !== null) {
        const ends = sessioni
            .map(s => s.endTime ? new Date(s.endTime).getTime() : null)
            .filter((t): t is number => t !== null);
        if (ends.length > 0) {
            const realFine = Math.max(...ends);
            totaleMs += Math.abs(realFine - pianFine);
        }
        // Nessuna sessione chiusa: si confronta solo l'inizio.
    }

    return Math.round(totaleMs / 60000);
};

export const hasOrariDifformi = (inc: Incarico | any, sogliaMin?: number | null): boolean => {
    if (sogliaMin == null || sogliaMin <= 0) return false;
    const diff = getOrariDifformiMinuti(inc);
    return diff !== null && diff > sogliaMin;
};

// Stessa allerta per i turni fiduciari. Il pianificato è dataTurno + oraInizio/oraFine
// (se oraFine < oraInizio la fine cade il giorno dopo), il reale è effettivoInizio/Fine.
// La soglia è Settings.sogliaOrariDifformiMin.
const parseOraSuData = (dataTurno: string | undefined, oraHhmm: string | undefined): Date | null => {
    if (!dataTurno || !oraHhmm) return null;
    const day = dataTurno.substring(0, 10);
    const timePart = /^\d{2}:\d{2}(:\d{2})?$/.test(oraHhmm)
        ? (oraHhmm.length === 5 ? `${oraHhmm}:00` : oraHhmm)
        : null;
    if (!timePart) return null;
    const d = new Date(`${day}T${timePart}`);
    return isNaN(d.getTime()) ? null : d;
};

export const getOrariDifformiMinutiTurno = (turno: any): number | null => {
    if (!turno) return null;
    const pianInizio = parseOraSuData(turno.dataTurno, turno.oraInizio);
    if (!pianInizio) return null;
    let pianFine = parseOraSuData(turno.dataTurno, turno.oraFine);
    if (pianFine && pianFine.getTime() < pianInizio.getTime()) {
        // Turno notturno: la fine va al giorno dopo.
        pianFine = new Date(pianFine.getTime() + 24 * 60 * 60 * 1000);
    }
    const realInizio = turno.effettivoInizio ? new Date(turno.effettivoInizio).getTime() : NaN;
    if (!Number.isFinite(realInizio)) return null;

    let totaleMs = Math.abs(realInizio - pianInizio.getTime());

    if (pianFine && turno.effettivoFine) {
        const realFine = new Date(turno.effettivoFine).getTime();
        if (Number.isFinite(realFine)) {
            totaleMs += Math.abs(realFine - pianFine.getTime());
        }
    }
    // Turno ancora in corso: si confronta solo l'inizio.
    return Math.round(totaleMs / 60000);
};

export const hasOrariDifformiTurno = (turno: any, sogliaMin?: number | null): boolean => {
    if (sogliaMin == null || sogliaMin <= 0) return false;
    const diff = getOrariDifformiMinutiTurno(turno);
    return diff !== null && diff > sogliaMin;
};

/**
 * Formatta uno scostamento in minuti mostrando solo le unità non nulle.
 * Es. 923 → "15h 23min", 1500 → "1g 1h", 0 → "0min".
 */
export const formatMinutiBreve = (min: number | null | undefined): string => {
    if (min == null || !Number.isFinite(min)) return '—';
    const abs = Math.abs(Math.round(min));
    const g = Math.floor(abs / 1440);
    const h = Math.floor((abs % 1440) / 60);
    const m = abs % 60;
    const parts: string[] = [];
    if (g > 0) parts.push(`${g}g`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 || parts.length === 0) parts.push(`${m}min`);
    return parts.join(' ');
};

// Cache a livello di modulo per leggere i Settings una volta sola (cambiano di rado).
// Due soglie separate: sogliaOrariDifformiIncarichiMin per gli incarichi (null =
// disattivata) e sogliaOrariDifformiMin per i turni fiduciari.
interface SoglieCache {
    incarichi: number | null;
    turni: number | null;
    // Se il RESPONSABILE può eliminare media senza approvazione (default false).
    responsabileDeleteDiretto: boolean;
}
let _soglieCache: SoglieCache | undefined = undefined;
let _soglieePromise: Promise<SoglieCache> | null = null;

const normalizzaSoglia = (raw: any): number | null => {
    const n = typeof raw === 'number' ? raw : (raw != null ? Number(raw) : NaN);
    return Number.isFinite(n) && n > 0 ? n : null;
};

const fetchSoglie = async (): Promise<SoglieCache> => {
    if (_soglieePromise) return _soglieePromise;
    _soglieePromise = (async () => {
        try {
            const res: any = await SettingsService.getAll();
            const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
            _soglieCache = {
                incarichi: normalizzaSoglia(list[0]?.sogliaOrariDifformiIncarichiMin),
                turni: normalizzaSoglia(list[0]?.sogliaOrariDifformiMin),
                responsabileDeleteDiretto: list[0]?.responsabilePuoEliminareMediaDiretto === true,
            };
            return _soglieCache;
        } catch {
            _soglieCache = { incarichi: null, turni: null, responsabileDeleteDiretto: false };
            return _soglieCache;
        }
    })();
    return _soglieePromise;
};

/**
 * Soglia "Orari difformi" per gli incarichi, in minuti. null = allerta disattivata.
 */
export const useSogliaOrariDifformi = (): number | null => {
    const [val, setVal] = useState<number | null>(_soglieCache?.incarichi ?? null);
    useEffect(() => {
        if (_soglieCache !== undefined) {
            setVal(_soglieCache.incarichi);
            return;
        }
        let cancelled = false;
        fetchSoglie().then(v => { if (!cancelled) setVal(v.incarichi); });
        return () => { cancelled = true; };
    }, []);
    return val;
};

/**
 * Soglia "Orari difformi" per i turni fiduciari, in minuti.
 */
export const useSogliaOrariDifformiTurni = (): number | null => {
    const [val, setVal] = useState<number | null>(_soglieCache?.turni ?? null);
    useEffect(() => {
        if (_soglieCache !== undefined) {
            setVal(_soglieCache.turni);
            return;
        }
        let cancelled = false;
        fetchSoglie().then(v => { if (!cancelled) setVal(v.turni); });
        return () => { cancelled = true; };
    }, []);
    return val;
};

/**
 * Indica se il RESPONSABILE può eliminare media direttamente (default false:
 * serve l'approvazione del titolare). Usa la stessa cache delle soglie.
 */
export const useResponsabileDeleteDirettoAbilitato = (): boolean => {
    const [val, setVal] = useState<boolean>(_soglieCache?.responsabileDeleteDiretto ?? false);
    useEffect(() => {
        if (_soglieCache !== undefined) {
            setVal(_soglieCache.responsabileDeleteDiretto);
            return;
        }
        let cancelled = false;
        fetchSoglie().then(v => { if (!cancelled) setVal(v.responsabileDeleteDiretto); });
        return () => { cancelled = true; };
    }, []);
    return val;
};
